import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';

interface CacheEntry {
  response: HttpResponse<unknown>;
  timestamp: number;
}

@Injectable()
export class CacheInterceptor implements HttpInterceptor {
  private cache = new Map<string, CacheEntry>();
  private readonly cacheTTL = 5 * 60 * 1000; // 5 minutes in milliseconds

  // URLs that should be cached (GET requests only)
  private readonly cacheableUrls = [
    '/api/hotels',
    '/api/destinations',
    '/api/amenities',
    '/api/room-types'
  ];

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Only cache GET requests for specific URLs
    if (req.method !== 'GET' || !this.shouldCache(req.url)) {
      return next.handle(req);
    }

    const cacheKey = this.getCacheKey(req);
    const cachedResponse = this.getFromCache(cacheKey);

    // Return cached response if available and not expired
    if (cachedResponse) {
      return of(cachedResponse);
    }

    // Make the request and cache the response
    return next.handle(req).pipe(
      tap(event => {
        if (event instanceof HttpResponse) {
          this.addToCache(cacheKey, event);
        }
      })
    );
  }

  private shouldCache(url: string): boolean {
    return this.cacheableUrls.some(cacheableUrl => url.includes(cacheableUrl));
  }

  private getCacheKey(req: HttpRequest<unknown>): string {
    // Create cache key from URL and query parameters
    const url = req.urlWithParams;
    return url;
  }

  private getFromCache(key: string): HttpResponse<unknown> | null {
    const entry = this.cache.get(key);
    
    if (!entry) {
      return null;
    }

    // Check if cache entry is expired
    const isExpired = Date.now() - entry.timestamp > this.cacheTTL;
    
    if (isExpired) {
      this.cache.delete(key);
      return null;
    }

    return entry.response;
  }

  private addToCache(key: string, response: HttpResponse<unknown>): void {
    const entry: CacheEntry = {
      response: response.clone(),
      timestamp: Date.now()
    };
    
    this.cache.set(key, entry);
    
    // Clean up expired entries periodically
    this.cleanupExpiredEntries();
  }

  private cleanupExpiredEntries(): void {
    const now = Date.now();
    
    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp > this.cacheTTL) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Clear all cached entries
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Clear specific cache entry
   */
  clearCacheEntry(key: string): void {
    this.cache.delete(key);
  }
}